import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  AmountInput,
  Icon,
  Label,
  LabelledNumberStepper
} from '@components/index';
import {
  GROUP_MEMBER_MAX_AMOUNT,
  GROUP_MEMBER_MIN_AMOUNT,
  SPLIT_REQUIRED_AMOUNT_MIN
} from '@constants/generic';
import { getInitialBulkPaymentDiscountData } from '@features/group-management/constants';
import { getFeeCategoryDataFromConfig } from '@features/group-management/helper';
import {
  applyHeaderAmountSplitToTerms,
  applyHeaderAmountSplitToTermsWithFeeCategories,
  getDefaultTermsListForCreate,
  onAddTerm,
  onTermDelete
} from '@features/group-management/helpers/customTerm';
import {
  BulkPaymentDiscountType,
  FeeCategoryDataType,
  FormDataType,
  MemberListType,
  TermsList,
  ValidationError
} from '@types';
import { formatISODate } from '@utils/date';
import {
  getGroupMemberMaxAmountErrorMessage,
  roundTo2,
  sanitizeNumberInput
} from '@utils/generic';

import BulkPaymentDiscount from '../bulk-payment-discount/BulkPaymentDiscount';
import CustomAmountSection from './CustomAmountSection';

import theme from '../../../../../../themes/colors';
import CustomTermTable from '../../v1/custom-term/CustomTermTable';
import FeeCategorySection from '../../v1/feeDetailsInput/FeeCategorySection';
import GroupDateSettings from '../../v1/feeDetailsInput/GroupDateSettings';
import InstallmentSection from '../../v1/feeDetailsInput/InstallmentSection';

const MAX_ADVANCED_TERM_INSTALLMENTS = 24;

interface FeeDetailsSectionProps {
  formData: FormDataType;
  onInputChange: (fieldName: string, value: any) => void;
  memberList?: MemberListType[] | [];
  setIsFeeCategoryEnabled: (value: boolean) => void;
  feeCategories: FeeCategoryDataType[];
  setFeeCategories: React.Dispatch<React.SetStateAction<FeeCategoryDataType[]>>;
  isFeeCategoryEnabled: boolean;
  branchDetails?: any;
  customTermDueDateError?: ValidationError;
  setCustomTermDueDateError?: (value: ValidationError) => void;
  error?: ValidationError;
  setError?: (value: ValidationError) => void;
  bulkPaymentDiscountRowErrors?: string[];
  onClearBulkPaymentDiscountRowErrors?: () => void;
  isEditGroup?: boolean;
  onPaymentLinkSentChange?: (isSent: boolean) => void;
}

const FeeDetailsSection = (props: FeeDetailsSectionProps) => {
  const {
    formData,
    onInputChange,
    memberList,
    setIsFeeCategoryEnabled,
    feeCategories,
    setFeeCategories,
    isFeeCategoryEnabled,
    branchDetails,
    customTermDueDateError,
    setCustomTermDueDateError,
    error,
    setError,
    bulkPaymentDiscountRowErrors = [],
    onClearBulkPaymentDiscountRowErrors,
    isEditGroup = false,
    onPaymentLinkSentChange
  } = props;

  const [feeCategory, setFeeCategory] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<string>(
    isEditGroup && formData.startDate
      ? formData.startDate
      : new Date().toString()
  );
  const splitHeaderToTermsRef = useRef(false);
  const [splitHeaderToTerms, setSplitHeaderToTerms] = useState(false);

  const { orgConfig } = useSelector((state: any) => state.user);

  const { groupId } = useParams();
  const groupMemberMaxAmount =
    orgConfig?.group_member_amount_max || GROUP_MEMBER_MAX_AMOUNT;
  const groupMemberMinAmount =
    orgConfig?.group_member_amount_min || GROUP_MEMBER_MIN_AMOUNT;
  const splitRequiredMinAmount =
    orgConfig?.split_required_amount_min || SPLIT_REQUIRED_AMOUNT_MIN;

  const defaultTermLabel: string =
    branchDetails?.default_term_label || 'Installment';

  const defaultTermCategories = (
    feeCategoryData: {
      id: number;
      label: string;
      value: string;
    }[]
  ) =>
    feeCategoryData.map((item: any) => ({
      branch_fee_category_id: item.id,
      account_detail_id: item.id,
      category: item.label,
      amount: item.value
    }));

  const updateTermsList = (
    termsList: TermsList[],
    redistributeFromHeader?: boolean
  ) => {
    let next = termsList;
    if (
      formData.isCustomTerm &&
      !isEditGroup &&
      splitHeaderToTermsRef.current &&
      redistributeFromHeader
    ) {
      const splitFn = isFeeCategoryEnabled
        ? applyHeaderAmountSplitToTermsWithFeeCategories
        : applyHeaderAmountSplitToTerms;
      next = splitFn(termsList, Number(formData.amount) || 0);
    }
    if (formData.isCustomTerm && next.length > 0 && formData.endDate) {
      const lastDueRaw = next[next.length - 1].dueDate;
      if (lastDueRaw) {
        const lastDue = new Date(
          new Date(lastDueRaw).setHours(0, 0, 0, 0)
        ).getTime();
        const groupEnd = new Date(
          new Date(formData.endDate).setHours(0, 0, 0, 0)
        ).getTime();
        if (lastDue > groupEnd) {
          setError?.({
            ...error,
            endDate:
              'Terms cannot extend beyond the selected Group Deactivation Date'
          });
        } else {
          setError?.({ ...error, endDate: '' });
        }
      }
    }
    onInputChange('termsList', next);
  };

  const handleEndSplitHeaderAmountMode = useCallback(() => {
    splitHeaderToTermsRef.current = false;
    setSplitHeaderToTerms(false);
  }, []);

  const handleStartSplitHeaderAmountMode = useCallback(() => {
    splitHeaderToTermsRef.current = true;
    setSplitHeaderToTerms(true);
  }, []);

  const onAmountChange = (value: string) => {
    setError?.({ ...error, amount: '' });
    const sanitizedAmount = sanitizeNumberInput(value, true);
    if (Number(sanitizedAmount) > Number(groupMemberMaxAmount)) {
      setError?.({
        ...error,
        amount: getGroupMemberMaxAmountErrorMessage(groupMemberMaxAmount)
      });
    }
    if (formData.isCustomTerm && !isEditGroup) {
      splitHeaderToTermsRef.current = true;
      setSplitHeaderToTerms(true);
      onInputChange('amount', sanitizedAmount === '' ? '' : sanitizedAmount);
      const baseList =
        formData.termsList && formData.termsList.length > 0
          ? formData.termsList
          : getDefaultTermsListForCreate(defaultTermLabel);
      updateTermsList(
        applyHeaderAmountSplitToTerms(baseList, Number(sanitizedAmount) || 0)
      );
    } else {
      onInputChange('amount', sanitizedAmount);
    }
    if (
      Number(sanitizedAmount) < Number(splitRequiredMinAmount) &&
      formData?.paymentInstallment
    )
      onInputChange('paymentInstallment', false);
  };

  const onAmountBlur = (value: string) => {
    if (
      value.length &&
      Number(value) !== 0 &&
      Number(value) < Number(groupMemberMinAmount)
    )
      setError?.({
        ...error,
        amount: `Minimum amount should be ₹${groupMemberMinAmount}`
      });

    if (value.length && Number(value) > Number(groupMemberMaxAmount))
      setError?.({
        ...error,
        amount: getGroupMemberMaxAmountErrorMessage(groupMemberMaxAmount)
      });
  };

  const onCustomAmountEnabledChange = (enabled: boolean) => {
    onInputChange('paymentInstallment', enabled);
  };

  const onBulkPaymentDiscountEnabledChange = (enabled: boolean) => {
    onClearBulkPaymentDiscountRowErrors?.();
    onInputChange('isBulkPaymentDiscountEnabled', enabled);
    if (!enabled) {
      return;
    }
    if (
      !formData.bulkPaymentDiscountData ||
      formData.bulkPaymentDiscountData.length === 0
    ) {
      onInputChange(
        'bulkPaymentDiscountData',
        getInitialBulkPaymentDiscountData()
      );
    }
  };

  const onBulkPaymentDiscountDataChange = (rows: BulkPaymentDiscountType[]) => {
    onClearBulkPaymentDiscountRowErrors?.();
    onInputChange('bulkPaymentDiscountData', rows);
  };

  const onAdvancedTermInstallmentCountChange = (targetCount: number) => {
    if (!formData.isCustomTerm) return;

    const minTerms = 2;
    const nextCount = Math.min(
      MAX_ADVANCED_TERM_INSTALLMENTS,
      Math.max(minTerms, targetCount)
    );

    const currentList =
      formData.termsList && formData.termsList.length > 0
        ? [...formData.termsList]
        : getDefaultTermsListForCreate(defaultTermLabel);

    if (nextCount === currentList.length) return;

    if (nextCount > currentList.length) {
      let list = currentList;
      while (list.length < nextCount) {
        const { term } = onAddTerm(list, feeCategories, defaultTermLabel);
        list = term;
      }
      if (
        splitHeaderToTermsRef.current &&
        !isEditGroup &&
        formData.isCustomTerm
      ) {
        const splitFn = isFeeCategoryEnabled
          ? applyHeaderAmountSplitToTermsWithFeeCategories
          : applyHeaderAmountSplitToTerms;
        updateTermsList(splitFn(list, Number(formData.amount) || 0));
      } else {
        updateTermsList(list);
      }
      return;
    }

    const lastTerm = currentList[currentList.length - 1];
    const { termsList: updatedList, totalAmount } = onTermDelete(
      lastTerm.id,
      currentList
    );
    if (
      splitHeaderToTermsRef.current &&
      !isEditGroup &&
      formData.isCustomTerm
    ) {
      const splitFn = isFeeCategoryEnabled
        ? applyHeaderAmountSplitToTermsWithFeeCategories
        : applyHeaderAmountSplitToTerms;
      updateTermsList(splitFn(updatedList, Number(formData.amount) || 0));
    } else {
      updateTermsList(updatedList);
      onInputChange('amount', totalAmount === 0 ? '' : String(totalAmount));
    }
  };

  useEffect(() => {
    if (formData.termsList && formData.isCustomTerm) {
      let totalAmountRaw = 0;

      const updatedTermsList = formData.termsList.map((term) => {
        let { feeCategoryData } = term;
        const { dueAmount } = term;
        totalAmountRaw += Number(dueAmount);

        if (feeCategories && (!feeCategoryData || feeCategoryData.length === 0))
          feeCategoryData = defaultTermCategories(feeCategories);

        if (isFeeCategoryEnabled && feeCategoryData) {
          const termFeeCategoryData =
            getFeeCategoryDataFromConfig(feeCategoryData);
          const totalFeeConfigAmount = roundTo2(
            termFeeCategoryData.reduce(
              (sum, category) => sum + (Number(category.value) || 0),
              0
            )
          );

          if (Number(dueAmount) !== totalFeeConfigAmount)
            feeCategoryData = feeCategoryData.map((category, index) => ({
              ...category,
              amount: index === 0 ? dueAmount : '0'
            }));
        }

        return {
          ...term,
          feeCategoryData
        };
      });

      const totalAmount = roundTo2(totalAmountRaw);
      const shouldSyncAmountFromTerms = isEditGroup || !splitHeaderToTerms;

      if (shouldSyncAmountFromTerms) {
        onInputChange('amount', totalAmount === 0 ? '' : String(totalAmount));
      }
      updateTermsList(updatedTermsList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(feeCategories),
    formData.isCustomTerm,
    isFeeCategoryEnabled,
    splitHeaderToTerms,
    isEditGroup
  ]);

  useLayoutEffect(() => {
    if (!formData.isCustomTerm) {
      splitHeaderToTermsRef.current = false;
      setSplitHeaderToTerms(false);
      return;
    }
    if (isEditGroup) {
      splitHeaderToTermsRef.current = false;
      setSplitHeaderToTerms(false);
      return;
    }
    splitHeaderToTermsRef.current = true;
    setSplitHeaderToTerms(true);
  }, [formData.isCustomTerm, isEditGroup]);

  useEffect(() => {
    setFeeCategory(isFeeCategoryEnabled);
  }, [isFeeCategoryEnabled, feeCategories]);

  useEffect(() => {
    if (formData.startDate && formData.startDate !== startDate) {
      setStartDate(
        formatISODate({
          date: new Date(formData.startDate),
          separator: '-',
          format: 'yyyy-mm-dd'
        })
      );
      if (isEditGroup) {
        onInputChange('startDate', formData.startDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.startDate]);

  useEffect(() => {
    if (!isEditGroup && formData.isCustomTerm && !formData.startDate) {
      const formattedStartDate = formatISODate({
        date: new Date(),
        separator: '-',
        format: 'yyyy-mm-dd'
      });
      setStartDate(formattedStartDate);
      onInputChange('startDate', formattedStartDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.isCustomTerm]);

  return (
    <div className="rounded-xl border border-cofeeGreyBlue200 bg-cofeeNeutral1 py-4">
      <h3 className="border-b border-cofeeGreyBlue200 px-6 pb-4 text-lg font-semibold text-cofeeNeutral10">
        Fee details
      </h3>
      <div className="grid gap-y-4 p-6">
        {/* Frequency / Date Settings */}
        <GroupDateSettings
          formData={formData}
          groupId={groupId || ''}
          startDate={startDate}
          setStartDate={setStartDate}
          onInputChange={onInputChange}
          error={error}
          setError={setError}
          isEditGroup={isEditGroup}
          memberList={memberList}
          groupDateClassName="w-full md:w-1/2"
          groupStartDateClassName="w-full md:w-1/2"
          groupEndDateClassName="w-full md:w-1/2"
          byTermAdvancedDefaultLabel={branchDetails?.default_term_label}
          onPaymentLinkSentChange={onPaymentLinkSentChange}
        />

        {/* Amount */}
        <div className="w-full md:w-1/2 lg:w-1/3">
          <Label label="Amount" className="!mb-2 !text-content" isRequired />
          <AmountInput
            value={formData.amount}
            onAmountChange={onAmountChange}
            handleInputBlur={onAmountBlur}
            error={!!error?.amount}
            errorMessage={error?.amount || ''}
            disabled={
              feeCategory ||
              (formData.isCustomTerm && (isEditGroup || !splitHeaderToTerms))
            }
            className="!bg-white"
            inputClassNames={`${
              feeCategory ||
              (formData.isCustomTerm && (isEditGroup || !splitHeaderToTerms))
                ? 'cursor-not-allowed opacity-40'
                : ''
            }`}
            maxAmount={groupMemberMaxAmount}
            allowDecimals
            allowZero
          />
          {!formData.isCustomTerm && (
            <div
              className={`${
                error?.amount ? 'mt-6' : 'mt-1.5'
              } flex items-center gap-1 text-sm text-grey60`}
            >
              <Icon name="alert-circle" stroke={theme.subText} size="16" />
              {`You can enter 0 or any amount between ${groupMemberMinAmount} and ${groupMemberMaxAmount}`}
            </div>
          )}
        </div>
        {formData.isCustomTerm && (
          <LabelledNumberStepper
            label={`Number of ${defaultTermLabel?.toLowerCase() || ''}s`}
            isRequired
            value={formData.termsList?.length || 2}
            onChange={onAdvancedTermInstallmentCountChange}
            minValue={2}
            className="w-full md:w-1/2 lg:w-1/3"
            disabled={isEditGroup}
          />
        )}

        <div className="my-2 h-px w-full bg-cofeeGreyBlue200" />

        {/* Fee Category */}
        <FeeCategorySection
          isCustomTerm={!!formData.isCustomTerm}
          isEditGroup={isEditGroup}
          setIsFeeCategoryEnabled={setIsFeeCategoryEnabled}
          setFeeCategory={setFeeCategory}
          groupId=""
          feeCategories={feeCategories}
          setFeeCategories={setFeeCategories}
          onInputChange={onInputChange}
          isFeeConfigured={
            isEditGroup
              ? formData?.feeCategoryEnabled
              : branchDetails?.is_fee_configured
          }
          isFeeCategoryEnabled={isFeeCategoryEnabled}
          feeCategory={feeCategory}
          groupMemberMaxAmount={groupMemberMaxAmount}
          error={error}
          setError={setError}
        />

        {/* Custom Term Table */}
        {formData.isCustomTerm ? (
          <>
            <CustomTermTable
              termsList={
                formData.termsList ||
                getDefaultTermsListForCreate(defaultTermLabel)
              }
              setTermsList={updateTermsList}
              isFeeCategoryEnabled={isFeeCategoryEnabled}
              feeCategories={feeCategories}
              onAmountChange={(amount) => onInputChange('amount', amount)}
              isEditGroup={isEditGroup}
              setError={setCustomTermDueDateError}
              error={customTermDueDateError}
              groupEndDateValidationMessage={error?.endDate}
              splitHeaderAmountToTerms={splitHeaderToTerms}
              onEndSplitHeaderAmountMode={handleEndSplitHeaderAmountMode}
              onStartSplitHeaderAmountMode={handleStartSplitHeaderAmountMode}
              defaultTermLabel={defaultTermLabel}
            />
            <div className="my-2 h-px w-full bg-cofeeGreyBlue200" />
            <CustomAmountSection
              formData={formData}
              onCustomAmountEnabledChange={onCustomAmountEnabledChange}
            />

            <>
              <div className="my-2 h-px w-full bg-cofeeGreyBlue200" />
              <BulkPaymentDiscount
                formData={formData}
                bulkPaymentTierRowErrors={bulkPaymentDiscountRowErrors}
                onBulkPaymentDiscountEnabledChange={
                  onBulkPaymentDiscountEnabledChange
                }
                onBulkPaymentDiscountDataChange={
                  onBulkPaymentDiscountDataChange
                }
              />
            </>
          </>
        ) : (
          <InstallmentSection
            formData={formData}
            onInputChange={onInputChange}
            isEnabled={false} // Update this true if installment allow for always
          />
        )}
      </div>
    </div>
  );
};

export default FeeDetailsSection;
